import React, { useCallback } from 'react';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Button, Grid } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';

import BasicTooltip from 'UI/components/BasicTooltip/BasicTooltip';
import CommentSuggestionBox from 'UI/components/CommentSuggestionBox/CommentSuggestionBox';
import ConfirmationBasicDialog from 'UI/components/ConfirmationBasicDialog/ConfirmationBasicDialog';
import { useConfirmationDialog } from 'UI/components/ConfirmationDialog/useConfirmationDialog';
import SkillExpertiseEmployeePendingTooltip from 'UI/components/SkillExpertise/SkillExpertiseEmployeePendingTooltip';
import useDeleteAllAssignedSkillValidations from 'UI/containers/SkillValidation/AllAssignedSkills/useDeleteAllAssignedSkillValidations';
import { useMessageContext } from 'UI/contexts';
import useConfig from 'UI/customHooks/useConfig';
import { useT } from 'UI/lib';

const DialogContent = ({ information, comment, managerName }) => {
  return (
    <Grid container spacing={8}>
      <Grid item xs={12}>
        <DialogContentText id="confirmation-dialog-description">{information}</DialogContentText>
      </Grid>
      {comment && (
        <Grid item xs={12}>
          <CommentSuggestionBox comment={comment} managerName={managerName} />
        </Grid>
      )}
    </Grid>
  );
};
const SkillExpertiseEmployeeRemoval = ({
  handleDelete = () => {},
  supportedLevels,
  actualLevel,
  skillName,
  skillId,
  suggestedBy,
  comment,
}) => {
  const t = useT();
  const message = useMessageContext();
  const config = useConfig();
  const skillsPoliciesPreset =
    config.cms?.components?.SelfAssignedSkills?.skillSynonymPoliciesPreset;

  const { deleteSkillValidations } = useDeleteAllAssignedSkillValidations({ skillsPoliciesPreset });

  const { isDialogOpen, handleDialogClose, handleDialogOpen } = useConfirmationDialog();

  const handleDialogAction = useCallback(() => {
    handleDelete();
    handleDialogClose();
  }, []);

  const handleDialogReject = useCallback(() => {
    deleteSkillValidations({
      skillIdsToDelete: skillId,
      shouldGetViewerSkills: true,
    }).then(() => {
      message.setMessage({
        type: 'info',
        text: `${t('restrict:skills.message.skill_suggestion_rejected')} ✖`,
      });
    });
    handleDialogClose();
  }, [skillId]);

  return (
    <>
      <BasicTooltip placement="top" arrow title={<SkillExpertiseEmployeePendingTooltip />}>
        <Button
          sx={{
            border: '1px solid #D32F2F',
            backgroundColor: '#D32F2F33',
            borderRadius: '4px',
            padding: '2px 10px',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '5px',
          }}
          onClick={handleDialogOpen}
          aria-label="Pending approval button"
        >
          <Grid item container justifyContent="center">
            <Grid item container justifyContent="center">
              {supportedLevels.map((supportedLevel, index) => (
                <Grid key={supportedLevel} sx={{ padding: 0, lineHeight: 1 }}>
                  {index <= supportedLevels.indexOf(actualLevel) ? (
                    <StarIcon
                      data-test={`star-icon${index}`}
                      sx={{
                        color: '#D32F2F',
                      }}
                    />
                  ) : (
                    <StarBorderIcon
                      data-test={`star-icon${index}`}
                      sx={{
                        color: '#D32F2F',
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            <Grid item>{t('restrict:manager_assessment_status.delete')}</Grid>
          </Grid>
        </Button>
      </BasicTooltip>
      <ConfirmationBasicDialog
        isOpen={isDialogOpen}
        handleClose={() => handleDialogClose()}
        handleConfirm={() => handleDialogAction()}
        handleReject={() => handleDialogReject()}
        contentText="restrict:skills.dialog.skill_confirm_removal_content"
        titleText="restrict:company_view.dialog.skill_suggestion_removal_title"
        contentElements={{
          managerName: suggestedBy,
          skillName: skillName,
        }}
        cancelButtonText="common:reject"
        contentComponent={
          <DialogContent
            information={t('restrict:skills.dialog.skill_confirm_removal_content', {
              managerName: suggestedBy,
              skillName: skillName,
            })}
            comment={comment}
            managerName={suggestedBy}
          />
        }
      />
    </>
  );
};

export default SkillExpertiseEmployeeRemoval;
